import { useCallback, useMemo, useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { supabase, type Child, type Subject } from "@/lib/supabase";
import { queryClient } from "@/lib/queryClient";
import { useAuth } from "./useAuth";
import { useActiveSpace } from "./useActiveSpace";
import { useCurrentRole } from "./useCurrentRole";

export function getChildAge(birthdate: string | null): string {
  if (!birthdate) return "";
  const birth = new Date(birthdate);
  const now = new Date();
  let years = now.getFullYear() - birth.getFullYear();
  let months = now.getMonth() - birth.getMonth();
  if (now.getDate() < birth.getDate()) months--;
  if (months < 0) { years--; months += 12; }
  const totalMonths = years * 12 + months;
  const yrs = Math.floor(totalMonths / 12);
  const mos = totalMonths % 12;
  if (yrs === 0) return `${mos}m`;
  if (mos === 0) return `${yrs}y`;
  return `${yrs}y ${mos}m`;
}

const STORAGE_KEY = "mv_active_child_id";

/** Invalidate all children queries. Call after any mutation that adds/edits/removes children. */
export function invalidateChildren() {
  return queryClient.invalidateQueries({ queryKey: ['children'] });
}

async function fetchSubjectsForSpace(spaceId: string): Promise<Subject[]> {
  // Filter by active space so Members of multiple spaces never see a mixed
  // list. RLS would otherwise return the union of every space the user has
  // access to. The second query resolves which promoted children actually have
  // a live "Creator" login — the bridge column alone isn't enough because a
  // kid whose access was revoked keeps the column set but their space_members
  // row flips to status='removed'.
  const [subjectsRes, childMembersRes] = await Promise.all([
    supabase
      .from("children")
      .select("*")
      .eq("space_id", spaceId)
      .order("created_at", { ascending: true }),
    supabase
      .from("space_members")
      .select("id")
      .eq("space_id", spaceId)
      .eq("role", "child")
      .eq("status", "active"),
  ]);

  if (subjectsRes.error) console.error("useChildren:", subjectsRes.error.message);
  if (childMembersRes.error) console.error("useChildren (child members):", childMembersRes.error.message);

  const activeChildMemberIds = new Set(
    (childMembersRes.data ?? []).map((m) => (m as { id: string }).id),
  );

  return ((subjectsRes.data as unknown as Subject[]) ?? []).map((s) => ({
    ...s,
    hasActiveChildAccount:
      s.subject_type === "child" &&
      !!s.promoted_to_space_member_id &&
      activeChildMemberIds.has(s.promoted_to_space_member_id),
  }));
}

export function useChildren() {
  const { user } = useAuth();
  const { space: activeSpace } = useActiveSpace();
  const { role: currentRole, childIds: scopedChildIds } = useCurrentRole();
  const userId = user?.id;
  const activeSpaceId = activeSpace?.id ?? null;

  const [activeChildId, setActiveChildIdState] = useState<string | null>(
    () => localStorage.getItem(STORAGE_KEY),
  );

  const query = useQuery({
    queryKey: ['children', userId, activeSpaceId],
    enabled: !!userId && !!activeSpaceId,
    queryFn: async () => {
      const list = await fetchSubjectsForSpace(activeSpaceId!);
      // Seed the active child from the first child if nothing stored yet
      const childList = list.filter((s) => s.subject_type === 'child');
      if (childList.length > 0) {
        const stored = localStorage.getItem(STORAGE_KEY);
        if (!stored || !childList.find((c) => c.id === stored)) {
          localStorage.setItem(STORAGE_KEY, childList[0].id);
          setActiveChildIdState(childList[0].id);
        }
      }
      return list;
    },
  });

  const setActiveChild = useCallback((childId: string) => {
    localStorage.setItem(STORAGE_KEY, childId);
    setActiveChildIdState(childId);
  }, []);

  // For contributors the space_members row may carry a `child_ids[]` scope —
  // when populated, the contributor is allowed to tag and record for only
  // those specific children. Empty `child_ids` = all children in the space
  // (mirrors process-memory's empty-list-as-wildcard semantics).
  const scopedSubjects = useMemo(() => {
    const allSubjects = query.data ?? [];
    if (currentRole !== "contributor" || scopedChildIds.length === 0) {
      return allSubjects;
    }
    const allowed = new Set(scopedChildIds);
    return allSubjects.filter((s) => allowed.has(s.id));
  }, [query.data, currentRole, scopedChildIds]);

  // Children-only view preserves the legacy semantic for every existing
  // consumer (kid login, birthday detection, child_connections, prompts,
  // promotion, content-library age gating). Pets/toys live on `pets` and
  // the union on `subjects`.
  const children = useMemo(
    () => scopedSubjects.filter((s): s is Child => s.subject_type === 'child'),
    [scopedSubjects],
  );
  const pets = useMemo(
    () => scopedSubjects.filter((s) => s.subject_type === 'pet'),
    [scopedSubjects],
  );

  const activeChild =
    children.find((c) => c.id === activeChildId) ?? children[0] ?? null;

  return {
    data: query.data,
    children,
    pets,
    subjects: scopedSubjects,
    isLoading: query.isLoading,
    isError: query.isError,
    error: query.error as Error | null,
    refetch: query.refetch,
    refresh: query.refetch,
    activeChild,
    setActiveChild,
  };
}
